import { ScoredStock, StockFactors } from '@/lib/compute';
import { factorLabel } from '@/lib/clientScore';
import type { PillarMapping } from '@/lib/factor-config';
import { fmtVal } from '@/lib/stock-detail-helpers';

interface Props {
  stock: ScoredStock;
  pillarMapping: PillarMapping;
  getZScore: (key: keyof StockFactors) => number | null;
  scoreColor: (v: number | null) => string;
}

export function KeyDriversPanel({ stock, pillarMapping, getZScore, scoreColor }: Props) {
  const allFactors = Object.values(pillarMapping).flat();
  const scored = allFactors
    .map(f => ({
      key: f.key as keyof StockFactors,
      invert: f.invert,
      raw: stock.factors[f.key as keyof StockFactors],
      z: getZScore(f.key as keyof StockFactors),
    }))
    .filter(f => f.z !== null && f.raw !== null && f.raw !== undefined)
    .map(f => ({ ...f, effectiveZ: f.invert ? -f.z! : f.z! }))
    .sort((a, b) => b.effectiveZ - a.effectiveZ);

  const top5 = scored.slice(0, 5);
  const bottom5 = scored.slice(-5).reverse();

  const renderRow = (f: typeof scored[0]) => (
    <div key={f.key} className="flex items-center justify-between text-[10px] py-0.5">
      <span className="text-[#94a3b8] truncate" style={{ maxWidth: 180 }}>{factorLabel(f.key as string)}</span>
      <span className="flex items-baseline gap-2">
        <span className="text-[#e2e8f0] tabular-nums" style={{ fontFamily: 'var(--font-mono)', minWidth: 60, textAlign: 'right' }}>{fmtVal(f.raw as number, f.key as string)}</span>
        <span className="tabular-nums font-semibold" style={{ color: scoreColor(f.effectiveZ), fontFamily: 'var(--font-mono)', minWidth: 44, textAlign: 'right' }}>
          {f.effectiveZ >= 0 ? '+' : ''}{f.effectiveZ.toFixed(2)}σ
        </span>
      </span>
    </div>
  );

  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0] mb-2">Key Drivers</div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="text-[9px] font-semibold uppercase tracking-widest mb-1.5" style={{ color: '#4ade80' }}>Top 5 Strengths</div>
          <div>{top5.map(renderRow)}</div>
        </div>
        <div>
          <div className="text-[9px] font-semibold uppercase tracking-widest mb-1.5" style={{ color: '#f87171' }}>Bottom 5 Weaknesses</div>
          <div>{bottom5.map(renderRow)}</div>
        </div>
      </div>
    </div>
  );
}
